/**
 * Centralized API Configuration
 * Single source of truth for all API-related settings
 */

export const API_CONFIG = {
  /**
   * Get base URL based on environment
   */
  getBaseURL() {
    if (process.env.NODE_ENV === 'development') {
      return ''; // Empty string for development - proxy handles routing
    }
    
    // Production URL mapping
    return window.location.origin === "https://bus.little.africa"
      ? "https://bus.little.africa/server/api"
      : "";
  },

  /**
   * Build complete URL for API requests
   * Handles both development proxy and production URLs
   */
  buildURL(endpoint) {
    const baseURL = this.getBaseURL();
    
    if (process.env.NODE_ENV === 'development') {
      // Development: use proxy routing
      // Skip /api prefix for /new_api endpoints (they have their own proxy)
      if (endpoint.startsWith('/new_api/')) {
        return endpoint;
      }
      // Ensure /api prefix for standard endpoints
      if (endpoint.startsWith('/api/')) {
        return endpoint;
      }
      return endpoint.startsWith('/') ? `/api${endpoint}` : `/api/${endpoint}`;
    }
    
    // Production: handle different API backends
    if (endpoint.startsWith('/new_api/')) {
      // New backend endpoints go to corporate.little.africa
      const cleanEndpoint = endpoint.substring(8); // Remove '/new_api/'
      return `https://corporate.little.africa/new_backend/api/${cleanEndpoint}`;
    }
    
    // Standard endpoints: clean endpoint and append to base
    const cleanEndpoint = endpoint.startsWith('/api/') 
      ? endpoint.substring(4) 
      : endpoint.startsWith('/') 
        ? endpoint.substring(1) 
        : endpoint;
    
    return `${baseURL}/${cleanEndpoint}`;
  },

  /**
   * Default configuration for requests
   */
  getDefaultHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    // Add authentication token if available (support legacy and new keys)
    const token =
      localStorage.getItem('authToken') || // New key used by AuthService (STORAGE_KEYS.USER_TOKEN)
      localStorage.getItem('token') ||
      sessionStorage.getItem('authToken') ||
      sessionStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  },

  /**
   * Cache configuration
   */
  CACHE: {
    DEFAULT_TTL: 300000, // 5 minutes
    GET_REQUEST_TTL: 300000, // 5 minutes for GET requests
    USER_DATA_TTL: 600000, // 10 minutes for user data
  },

  /**
   * Request timeout configuration
   */
  TIMEOUT: {
    DEFAULT: 30000, // 30 seconds
    UPLOAD: 120000, // 2 minutes for file uploads
    DOWNLOAD: 180000, // 3 minutes for downloads
  },

  /**
   * Environment-specific settings
   */
  isDevelopment() {
    return process.env.NODE_ENV === 'development';
  },

  isProduction() {
    return process.env.NODE_ENV === 'production';
  },

  /**
   * Debug logging for development
   */
  log(...args) {
    if (this.isDevelopment()) {
      console.log('[API_CONFIG]', ...args);
    }
  },

  /**
   * Test proxy configuration (development only)
   */
  async testProxy() {
    if (!this.isDevelopment()) {
      return;
    }

    try {
      const testURL = this.buildURL('/v1/test');
      this.log('Testing proxy with URL:', testURL);
      
      const response = await fetch(testURL, {
        method: 'GET',
        headers: this.getDefaultHeaders(),
      });
      
      if (response.ok) {
        this.log('Proxy configuration is working correctly');
      } else {
        this.log('Proxy test failed with status:', response.status);
      }
    } catch (error) {
      this.log('Proxy test error:', error.message);
    }
  }
};

// Auto-test proxy in development (disabled to reduce console noise)
// if (API_CONFIG.isDevelopment()) {
//   // Test proxy after a short delay to ensure app is initialized
//   setTimeout(() => {
//     API_CONFIG.testProxy();
//   }, 2000);
// }

export default API_CONFIG;
